/**
 * Super Admin Company Service
 * Handles company-related API calls for super administrators
 */
import API_CONFIG from '../../config';
import axios from 'axios';
import { getSuperAdminAuthHeader } from './superAdminAuthService';

const API_URL = API_CONFIG.BASE_URL;

/**
 * Get all companies with pagination
 * @param {number} page - Page number (0-based)
 * @param {number} size - Number of items per page
 * @param {string} sort - Field to sort by
 * @param {string} direction - Sort direction (ASC or DESC)
 * @param {string} filter - Filter by subscription status: 'all', 'free_trial', 'active_subscription', 'inactive'
 * @param {string} search - Search by name, email, phone, address, or tax number
 * @returns {Promise} Promise with paginated companies data
 */
export const getAllCompanies = async (page = 0, size = 10, sort = 'name', direction = 'ASC', filter = 'all', search = '') => {
  try {
    const params = {
      page,
      size,
      sort,
      direction
    };

    // Only add filter if it's not 'all' or empty
    if (filter && filter !== 'all') {
      params.filter = filter;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }

    const response = await axios.get(
      `${API_URL}/companies`,
      {
        params,
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching companies:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to fetch companies';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden. You do not have permission to view companies.');
        case 404:
          throw new Error('Companies endpoint not found.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to fetch companies (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Get company by ID
 * @param {number} id - Company ID
 * @returns {Promise} Promise with company data
 */
export const getCompanyById = async (id) => {
  try {
    const response = await axios.get(
      `${API_URL}/companies/${id}`,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching company:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to fetch company';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden.');
        case 404:
          throw new Error('Company not found.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to fetch company (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Update company
 * @param {number} id - Company ID
 * @param {object} data - Data to update
 * @returns {Promise} Promise with updated company data
 */
export const updateCompany = async (id, data) => {
  try {
    const response = await axios.put(
      `${API_URL}/companies/${id}`,
      data,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error updating company:', error);
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to update company');
    }
    throw new Error('Network error or server unavailable');
  }
};

/**
 * Get all users for a company (super admin only)
 * @param {number} companyId - Company ID
 * @returns {Promise} Promise with list of users (name, email, id, role, etc.)
 */
export const getCompanyUsers = async (companyId) => {
  const response = await axios.get(
    `${API_URL}/superadmin/companies/${companyId}/users`,
    { headers: getSuperAdminAuthHeader() }
  );
  return response.data;
};

/**
 * Update password for a user (super admin only - e.g. when user forgot password)
 * @param {number} userId - User ID
 * @param {string} newPassword - New password
 * @returns {Promise}
 */
export const updateUserPassword = async (userId, newPassword) => {
  const response = await axios.put(
    `${API_URL}/superadmin/users/${userId}/password`,
    { password: newPassword },
    { headers: getSuperAdminAuthHeader() }
  );
  return response.data;
};

/**
 * Get a login token for a company user so super admin can open the webapp as that user (no credentials).
 * @param {number} userId - User ID
 * @returns {Promise<{ accessToken: string, user: object }>}
 */
export const loginAsUser = async (userId) => {
  const response = await axios.post(
    `${API_URL}/superadmin/users/${userId}/login-as`,
    {},
    { headers: getSuperAdminAuthHeader() }
  );
  return response.data;
};

/**
 * Permanently delete a company and all related data (super admin only).
 * @param {number} companyId
 * @param {string} confirmName - must match company name (case-insensitive)
 */
export const deleteCompanyWithAllData = async (companyId, confirmName) => {
  await axios.delete(`${API_URL}/superadmin/companies/${companyId}`, {
    data: { confirmName },
    headers: {
      ...getSuperAdminAuthHeader(),
      'Content-Type': 'application/json',
    },
  });
};

// Export default service object
const companyService = {
  getAllCompanies,
  getCompanyById,
  updateCompany,
  getCompanyUsers,
  updateUserPassword,
  loginAsUser,
  deleteCompanyWithAllData,
};

export default companyService;

